/**
 * App-side wrapper around the framework's useBrowserBus hook.
 *
 * The framework hook is domain-agnostic — it handles SSE plumbing,
 * choreography dispatch, and the plan-event bridge. This wrapper injects
 * the CongressMCP-specific entity_type → tanstack-query-key map so live
 * updates propagate to the right React surfaces.
 *
 * Add a new case here when a new mutation needs to push live updates to
 * a React surface. (Mutations themselves call broadcastDataChanged on
 * the server side; this is just the receiving end's routing table.)
 */
import {
  useBrowserBus as useBrowserBusFramework,
  type KeysToInvalidate,
} from "../../framework/react/useBrowserBus.js";
import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import { LIVE_AI_TOOL_ACTIVITY_EVENT, type LiveToolActivity } from "../../framework/react/live-ai-session-events.js";
import { useWorkspace } from "./useWorkspace.js";
import { workspaceWorkflowKeys } from "../../lib/workspace-workflow-invalidation.js";

/** Federal ("119-hr-1") or state ("state:<source id>") bill refs. */
const BILL_REF_PATTERN = /^(\d+-[a-z]+-\d+|state:.+)$/;

export const keysToInvalidate: KeysToInvalidate = (ev) => {
  const keys: Array<Array<string | undefined>> = workspaceWorkflowKeys(ev);
  switch (ev.entity_type) {
    case "workspace_widgets":
      if (ev.workspace_id) keys.push(["widgets", ev.workspace_id]);
      break;
    case "workspace":
    case "workspace_member":
    case "workspaces":
      keys.push(["workspaces"]);
      keys.push(["personal-workspaces"]);
      keys.push(["account-status"]);
      keys.push(["mcp-capabilities"]);
      if (ev.workspace_id) keys.push(["workspace-info", ev.workspace_id]);
      keys.push(["session-kickoff"]);
      break;
    case "champions":
    case "member_groups":
      if (ev.workspace_id) {
        keys.push(["champions", ev.workspace_id]);
        // Member-group CRUD renders on Map dashboard, Whip board, and
        // Caucuses index — all keyed ["member-groups", workspace_id].
        keys.push(["member-groups", ev.workspace_id]);
        // The whip board's group filter reads the roster separately.
        keys.push(["group-roster", ev.workspace_id]);
      }
      break;
    case "electoral_targets":
      if (ev.workspace_id) {
        // Two query keys are in flight on the designations page (the
        // tab-level "targets" and the page-level Paint-button preview
        // "electoral-targets"). Invalidate both so neither stays stale.
        keys.push(["targets", ev.workspace_id]);
        keys.push(["electoral-targets", ev.workspace_id]);
      }
      break;
    case "attention_items":
      keys.push(["session-kickoff"]);
      // Bell-icon queue in the sidebar header — invalidate so the
      // count badge + dropdown contents stay live.
      if (ev.workspace_id) keys.push(["attention-bell", ev.workspace_id]);
      break;
    case "workspace_event":
    case "workspace_events":
      // The activity stream auto-refreshes whenever a new event lands.
      // Powers the dashboard timeline + per-entity history sidebars.
      // Shared workflow mapping covers both durable and actor vocabulary.
      break;
    case "dashboard_feed_panels":
      // Server-persisted dashboard feed panels — invalidate so a
      // newly-pushed panel appears in the feed without manual refresh.
      if (ev.workspace_id) keys.push(["dashboard-feed-panels", ev.workspace_id]);
      break;
    case "workspace_live_canvas":
      // Docked Live Canvas side panel on the app. render_inline_panel
      // writes to workspaces.live_canvas; the panel reads via
      // /api/v1/workspaces/:wsId/live-canvas keyed on ["live-canvas",
      // workspace_id]. Invalidate so the latest spec renders within
      // seconds without manual refresh.
      if (ev.workspace_id) keys.push(["live-canvas", ev.workspace_id]);
      break;
    case "living_reports":
      // Living reports (/app/reports/:slug, /app/presentations/:slug).
      // Broadcasts carry the slug (a save, or source_changed when a table
      // the report reads was written); durable outbox events carry the row
      // id, so those refresh every open report in the workspace.
      if (ev.workspace_id) {
        const slug = ev.entity_id && !/^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(ev.entity_id) ? ev.entity_id : null;
        keys.push(slug ? ["living-report", ev.workspace_id, slug] : ["living-report", ev.workspace_id]);
        if (ev.reason !== "source_changed") keys.push(["living-reports", ev.workspace_id]);
      }
      break;
    case "whip_stance":
    case "whip_board":
    case "whip_stance_proposal":
    case "whip_board_access":
    case "whip_board_publication":
    case "amendment_watch":
    case "workspace_provision":
      // Shared workflow mapping refreshes bill/issue boards, member detail,
      // and widgets. entity_id may be a member or stance row, not a bill ref.
      break;

    // ── CRM mutations ─────────────────────────────────────────────────
    // The Tier-1 broadcast sweep (commit TBD) added broadcastDataChanged
    // to every write tool that touches workspace data. The cases below
    // route those events to the queryKeys that need to refetch so the
    // page never goes stale on a write — kills the AOC-class bug across
    // the whole app, not just the whip board.

    case "contact":
      // contacts list (cmcp/app/pages/ContactsPage) + per-contact portal
      keys.push(["contacts-index"]);
      if (ev.entity_id) keys.push(["contact-portal", ev.workspace_id, ev.entity_id]);
      break;
    case "contact_affiliation":
    case "contact_note":
      // These bump the parent contact directly via a second broadcast,
      // but include the case here so that any future contact_affiliation/
      // contact_note specific surface picks up automatically.
      keys.push(["contacts-index"]);
      break;

    case "account":
      keys.push(["accounts-index"]);
      if (ev.entity_id) keys.push(["account-portal", ev.workspace_id, ev.entity_id]);
      break;

    case "issue":
      if (ev.workspace_id) {
        keys.push(["issues-index", ev.workspace_id]);
        keys.push(["widget", "issues.portfolio", ev.workspace_id]);
      }
      if (ev.entity_id) keys.push(["issue-portal", ev.workspace_id, ev.entity_id]);
      break;
    case "issue_bill_rating":
      // Ratings drive issue-portal weighted avgs. Issue-level bumps
      // already fire as a sibling broadcast; this case catches
      // anything keyed directly to ratings.
      if (ev.workspace_id) keys.push(["issues-index", ev.workspace_id]);
      break;

    case "engagement":
    case "activity":
      // Activities/engagements show up on the parent contact, account,
      // and member portals. The mutation tools fire a SECOND broadcast
      // per touched parent (contact / account / member) so those keys
      // invalidate naturally; this case handles any standalone
      // activity-list surface that emerges later.
      if (ev.workspace_id) keys.push(["contacts-index"]);
      break;

    case "member_touch":
      // Member-portal touch history + relationship widgets.
      keys.push(["top-relationships"]);
      if (ev.workspace_id) {
        keys.push(["widget", "field.recent_touches", ev.workspace_id]);
        keys.push(["widget", "field.tier_breakdown", ev.workspace_id]);
        keys.push(["widget", "field.never_engaged", ev.workspace_id]);
        keys.push(["widget", "members.cooling", ev.workspace_id]);
      }
      break;
    case "member":
    case "member_stock_disclosure":
    case "workspace_member_stock_disclosures":
      // The member dossier at /app/members/:bioguide. Bumped from
      // touches, engagements, activities, stock disclosures — anything that
      // changes a member's CRM footprint in this workspace.
      if (ev.entity_id) {
        keys.push(["member-portal", ev.workspace_id, ev.entity_id]);
        keys.push(["member-accountability", ev.workspace_id, ev.entity_id]);
        keys.push(["member-stock-disclosures", ev.workspace_id, ev.entity_id]);
      } else if (ev.workspace_id) {
        keys.push(["member-accountability", ev.workspace_id]);
        keys.push(["member-stock-disclosures", ev.workspace_id]);
      }
      if (ev.workspace_id) {
        keys.push(["bill-stock-conflicts", ev.workspace_id]);
      }
      keys.push(["members-index"]);
      keys.push(["top-relationships"]);
      break;

    case "project":
      // Tracked-bill list + the bill.* dashboard widgets.
      if (ev.workspace_id) {
        keys.push(["tracked-bills", ev.workspace_id]);
        keys.push(["notification-tracking", ev.workspace_id]);
        // Project ids in durable events are UUIDs; the bill portal uses a
        // canonical bill ref, so refresh the workspace prefix.
        keys.push(["bill-intelligence", ev.workspace_id]);
        keys.push(["legislation-detail", ev.workspace_id]);
        keys.push(["bill-record-status"]);
        keys.push(["tracked-bills-dash", ev.workspace_id]);
        keys.push(["widget", "bills.tracked_recent", ev.workspace_id]);
        keys.push(["widget", "bills.stalled", ev.workspace_id]);
      }
      break;

    case "briefing":
    case "briefing_item":
    case "briefing_items":
    case "crm_news_tasks":
    case "crm_news_comments":
    case "crm_news_reactions":
      keys.push(["briefings-list"]);
      keys.push(["newsroom", ev.workspace_id]);
      // An outbox row for briefing_items carries the item id, not its
      // parent briefing id. Prefix invalidation refreshes whichever
      // briefing is open and makes replayed events as live as actor-side
      // broadcasts.
      keys.push(["briefing-detail", ev.workspace_id]);
      keys.push(["contact-portal", ev.workspace_id]);
      keys.push(["account-portal", ev.workspace_id]);
      break;

    case "contact_social_profiles":
    case "contact_profile_quotes":
      // Durable outbox rows identify the child record. Refresh all open
      // contact dossiers under the workspace; immediate actor broadcasts
      // still target the exact contact.
      keys.push(["contacts-index"]);
      keys.push(["contact-portal", ev.workspace_id]);
      keys.push(["account-portal", ev.workspace_id]);
      break;

    case "crm_news_monitors":
      keys.push(["news-monitors", ev.workspace_id]);
      keys.push(["newsroom", ev.workspace_id]);
      break;

    case "policy_inbox_message":
    case "policy_intelligence_item":
    case "policy_intelligence_source":
      // The policy inbox is pushed by the user's authorized AI client.
      // Refresh the command center on both immediate actor broadcasts and
      // replayed database-outbox events so people can watch the scan land.
      if (ev.workspace_id) {
        keys.push(["policy-intelligence", ev.workspace_id]);
        keys.push(["policy-intelligence-summary", ev.workspace_id]);
        keys.push(["policy-intelligence-detail", ev.workspace_id]);
        keys.push(["policy-inbox-evidence", ev.workspace_id]);
        keys.push(["since-last-seen", ev.workspace_id]);
      }
      break;

    case "policy_inbox_source":
      // The source registry contains only non-secret labels and desired scan
      // cadence. Its exact queue is recalculated by the service-only work-plan
      // RPC; the connected authorized AI remains responsible for source reads.
      if (ev.workspace_id) {
        keys.push(["policy-inbox-work-plan", ev.workspace_id]);
      }
      break;

    case "policy_inbox_scan":
    case "policy_inbox_scan_receipt":
      // Scan runs and selected-message receipts are durable even while the
      // external AI is between MCP calls reading the authorized source. Keep
      // the coverage command center live independently of the transient tool
      // activity rail. A receipt can also change the visible story summary,
      // so refresh that compact dashboard view in the same pass.
      if (ev.workspace_id) {
        keys.push(["policy-inbox-scans", ev.workspace_id]);
        keys.push(["policy-inbox-work-plan", ev.workspace_id]);
        keys.push(["policy-intelligence", ev.workspace_id]);
        keys.push(["policy-intelligence-summary", ev.workspace_id]);
      }
      break;

    case "signal":
      // Signals show up on member-portal + bill detail. Most surfaces
      // refetch via the parent member/bill bump, but this case catches
      // standalone signals lists / signal-derivation views.
      if (ev.workspace_id) {
        keys.push(["session-kickoff"]);
      }
      break;

    case "proposed_tally":
    case "proposed_tally_entry":
      // Coalition tally proposals — the git-for-stances surface.
      // Invalidates the list view + the per-batch detail view + the
      // bill-portal whip-count panel that aggregates proposals.
      if (ev.workspace_id) {
        keys.push(["proposed-tallies", ev.workspace_id]);
        keys.push(["whip-count", ev.workspace_id]);
      }
      if (ev.entity_id) {
        keys.push(["proposed-tally", ev.entity_id]);
      }
      break;

    case "feed_post":
      // Coalition feed lives at one queryKey across both list-feed and
      // coalition-feed widgets — invalidate by workspace.
      if (ev.workspace_id) {
        keys.push(["coalition-feed", ev.workspace_id]);
        // Reply events carry the reply UUID, not its parent post id.
        keys.push(["feed-replies", ev.workspace_id]);
        keys.push(["widget", "coalition.recent_feed", ev.workspace_id]);
      }
      break;

    // ── Tier-2 mutations (added in the second sweep pass) ─────────────

    case "event":
      // Events page (when it lands) + per-project event lists.
      if (ev.workspace_id) keys.push(["events", ev.workspace_id]);
      break;

    case "goal":
      // Goals dashboard.
      if (ev.workspace_id) {
        keys.push(["goals", ev.workspace_id]);
        keys.push(["treasury-overview", ev.workspace_id]);
      }
      break;

    case "opportunity":
      // Opportunity portal + pipeline kanban.
      if (ev.workspace_id) {
        keys.push(["opportunities", ev.workspace_id]);
        keys.push(["pipeline-summary", ev.workspace_id]);
        keys.push(["treasury-overview", ev.workspace_id]);
      }
      if (ev.entity_id) keys.push(["opportunity-portal", ev.workspace_id, ev.entity_id]);
      break;

    case "scorecard":
      // Scorecards index + per-scorecard detail page + the per-member
      // alignment card on every member portal. We can't target a single
      // bioguide here (a scorecard run touches hundreds of members at
      // once), so the member-scorecards key is workspace-prefixed and
      // React Query invalidates every per-bioguide entry under it. That
      // means opening any member page right after a run reads fresh.
      //
      // The scorecard-results key is keyed only on scorecard_id (no
      // workspace prefix because the detail page never has a workspace
      // in its URL) — invalidate explicitly so the results table on the
      // open detail page refetches after auto-recompute lands. Without
      // this, the user adds 4 stances, the backend computes 547 member
      // results, but the detail page UI still says "Run the scorecard
      // to compute per-member scores" until manual refresh.
      // Also covers the member portal Scorecard Alignment card via the
      // member-scorecards prefix above.
      if (ev.workspace_id) {
        keys.push(["scorecards", ev.workspace_id]);
        keys.push(["member-scorecards", ev.workspace_id]);
      }
      if (ev.entity_id) {
        keys.push(["scorecard", ev.entity_id]);
        keys.push(["scorecard-results", ev.entity_id]);
        keys.push(["scorecard-stance-breakdown", ev.entity_id]);
      }
      break;

    case "report":
    case "report_share_token":
      // Reports index + per-report detail page. The share-token bump
      // refetches the same surfaces because tokens render inline.
      if (ev.workspace_id) keys.push(["reports", ev.workspace_id]);
      break;

    case "coalition":
      // Coalitions index + per-coalition portal. Coalition feed has
      // its own entity_type already.
      if (ev.workspace_id) keys.push(["coalitions-index", ev.workspace_id]);
      if (ev.entity_id) keys.push(["coalition-portal", ev.entity_id, ev.workspace_id]);
      break;

    // ── Long-tail entity_types ─────────────────────────────────────────

    case "treasury_account":
    case "ledger_entry":
    case "disbursement":
    case "budget":
    case "donation":
    case "pledge":
      // Treasury/ledger surface refetches on any of these. Most
      // share the same query keys (treasury page is a wholistic view).
      if (ev.workspace_id) {
        keys.push(["treasury", ev.workspace_id]);
        keys.push(["treasury-overview", ev.workspace_id]);
        keys.push(["widget", "treasury.unreconciled", ev.workspace_id]);
      }
      break;

    case "pipeline":
    case "stage":
      if (ev.workspace_id) {
        keys.push(["pipelines", ev.workspace_id]);
        keys.push(["treasury-overview", ev.workspace_id]);
      }
      break;

    case "playbook":
    case "playbook_run":
      // AI Studio's scheduled-report list, the open report's run history
      // and the rendered run. A scheduled run finishing while the studio is
      // open lands without a refresh.
      if (ev.workspace_id) {
        keys.push(["playbooks", ev.workspace_id]);
        keys.push(["playbook-detail", ev.workspace_id]);
        keys.push(["playbook-run", ev.workspace_id]);
      }
      break;

    case "ai_panels":
      // AI-composed per-page panel specs. The React side reads
      // ai_panels via /api/v1/workspaces/:wsId/panels and renders via
      // <PanelRenderer />. Invalidate by workspace + the
      // page_kind:instance_id pair encoded in entity_id (matches how
      // compose_panel broadcasts).
      if (ev.workspace_id) {
        keys.push(["ai-panels", ev.workspace_id]);
        if (ev.entity_id) {
          // entity_id format: "page_kind:instance_id"
          const [pk, iid] = ev.entity_id.split(":");
          if (pk) keys.push(["ai-panel", ev.workspace_id, pk, iid ?? "default"]);
        }
      }
      break;

    // ── Broadcast-write sweep additions (FB-MP5NS2B2 followup) ─────────
    // Immediate bill-scoped writes can send a bill_ref, while durable
    // outbox events send child row IDs. Never assume they are equivalent.

    case "bill_note":
    case "bill_resource":
    case "organization_position":
      // Immediate broadcasts use bill refs; durable outbox events use
      // child-row UUIDs. A workspace prefix supports both without guessing
      // which identifier a producer supplied.
      if (ev.workspace_id) {
        keys.push(["bill-intelligence", ev.workspace_id]);
        keys.push(["legislation-detail", ev.workspace_id]);
      }
      break;

    case "legislation_subscription":
    case "legislation_subscriptions":
    case "legislation_change":
      // A source-proven change (new text version, enactment, action) can
      // move the bill's text/law/section status, which is not workspace
      // scoped: refresh it by bill ref when known, else every open bill.
      if (ev.entity_type === "legislation_change") {
        keys.push(ev.entity_id && BILL_REF_PATTERN.test(ev.entity_id) ? ["bill-record-status", ev.entity_id] : ["bill-record-status"]);
      }
      if (ev.workspace_id) {
        keys.push(["legislation-detail", ev.workspace_id]);
        keys.push(["bill-intelligence", ev.workspace_id]);
        keys.push(["tracked-bills", ev.workspace_id]);
        keys.push(["notification-tracking", ev.workspace_id]);
        keys.push(["legislation-monitors", ev.workspace_id]);
      }
      break;

    case "personal_legislation_profile":
    case "personal_legislation_profiles":
      // Account-scoped (no workspace): state, districts and interests on
      // /app/setup and the representative cards that read them.
      keys.push(["personal-legislation-profile"]);
      break;

    case "legislation_monitor":
    case "legislation_monitors":
    case "legislation_monitor_delivery":
    case "legislation_monitor_deliveries":
      if (ev.workspace_id) {
        keys.push(["legislation-monitors", ev.workspace_id]);
        keys.push(["notification-tracking", ev.workspace_id]);
        keys.push(["legislation-detail", ev.workspace_id]);
      }
      break;

    case "webhook_endpoint":
    case "workspace_webhook_endpoints":
      // Monitor pages list the workspace's endpoints and webhook receipts.
      if (ev.workspace_id) {
        keys.push(["webhook-endpoints", ev.workspace_id]);
        keys.push(["legislation-monitors", ev.workspace_id]);
      }
      break;

    case "research_item":
    case "workspace_research_items":
      // /app/research list + search results, and the session kickoff's
      // recent-research digest. Actor-side broadcasts use "research_item";
      // replayed outbox rows carry the table name.
      if (ev.workspace_id) {
        keys.push(["research-index", ev.workspace_id]);
        keys.push(["research-search", ev.workspace_id]);
      }
      keys.push(["session-kickoff"]);
      break;

    case "feedback_ticket":
      // /app/feedback list + session-kickoff (recent tickets surface).
      keys.push(["feedback-tickets"]);
      keys.push(["feedback-ticket"]);
      keys.push(["session-kickoff"]);
      break;

    case "notification":
    case "notifications":
    case "notification_preference":
    case "notification_preferences":
      // Preferences emit the notification event too. Refresh another tab's
      // settings while keeping workspace-specific updates in their scope.
      if (ev.workspace_id) {
        keys.push(["notifications", ev.workspace_id]);
        keys.push(["notification-bell", ev.workspace_id]);
        keys.push(["notification-preferences", ev.workspace_id]);
      } else {
        keys.push(["notifications"]);
        keys.push(["notification-bell"]);
        keys.push(["notification-preferences"]);
      }
      break;

    case "active_cohort":
    case "cohort":
      // Cohort overlay state — EntityRef components across the substrate
      // read this to highlight matching entities.
      if (ev.workspace_id) keys.push(["active-cohort", ev.workspace_id]);
      break;

    case "object_comment":
    case "object_tag":
    case "object_task":
      if (ev.workspace_id) {
        keys.push(["object-comments", ev.workspace_id]);
        keys.push(["object-tags", ev.workspace_id]);
        keys.push(["object-tasks", ev.workspace_id]);
      }
      break;

    case "cross_workspace_share":
      // Admin tool: share list on the workspace settings page.
      if (ev.workspace_id) keys.push(["cross-workspace-shares", ev.workspace_id]);
      break;

    case "signal_source_trust":
      // Source-trust admin page (per-organization).
      if (ev.entity_id) keys.push(["signal-source-trust", ev.entity_id]);
      break;

    case "news_mention":
      // Per-entity mentions feed (member / candidate / etc. portals).
      keys.push(["news-mentions"]);
      if (ev.workspace_id) keys.push(["news-mentions", ev.workspace_id]);
      break;

    case "committee_amendment_vote":
      // Markup substrate renders in AmendmentsPanel, keyed
      // ["committee-reports", workspaceId, billRef]. Committee data is
      // global (not workspace-scoped) and the broadcast may omit
      // workspace_id, so invalidate the whole committee-reports prefix —
      // writes here are rare enough that coarse is fine. Also refresh
      // the bill-intelligence aggregate when we know which bill.
      keys.push(["committee-reports"]);
      if (ev.workspace_id && ev.entity_id) {
        keys.push(["bill-intelligence", ev.workspace_id, ev.entity_id]);
      }
      break;

    default:
      // No mapping — the broadcast reaches the client but nothing
      // refetches, i.e. a silent live-propagation gap. Warn in dev so
      // new entity_types get a mapping the first time they fire.
      if ((import.meta as { env?: { DEV?: boolean } }).env?.DEV) {
        console.warn(
          `[useBrowserBus] no queryKey mapping for entity_type "${ev.entity_type}" — add a case in useBrowserBus.ts`,
        );
      }
      break;
  }
  return keys;
};

export function accountKeysForActivity(activity: LiveToolActivity): string[][] {
  if (activity.phase === "started") return [];
  const accountCheck = ["get_account_status", "whoami"].includes(activity.tool_name);
  const accessFailure = activity.phase === "failed"
    && ["plan_gate", "insufficient_scope", "scope_denied", "unauthorized"].includes(activity.error_code ?? "");
  return accountCheck || accessFailure ? [["account-status"], ["mcp-capabilities"]] : [];
}

export function useBrowserBus() {
  const { current } = useWorkspace();
  const queryClient = useQueryClient();
  useEffect(() => {
    const onActivity = (event: Event) => {
      const activity = (event as CustomEvent<LiveToolActivity>).detail;
      if (!activity) return;
      for (const queryKey of accountKeysForActivity(activity)) {
        void queryClient.invalidateQueries({ queryKey });
      }
    };
    window.addEventListener(LIVE_AI_TOOL_ACTIVITY_EVENT, onActivity);
    return () => window.removeEventListener(LIVE_AI_TOOL_ACTIVITY_EVENT, onActivity);
  }, [queryClient]);
  const location = useLocation();
  useBrowserBusFramework({
    keysToInvalidate,
    workspaceId: current?.id ?? null,
    pagePath: location.pathname,
  });
}
